import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { siteEvents } from '@/lib/siteEvents';

/**
 * One line under the salary on a job page: how the posting's pay compares
 * with the median for its role in its metro (or everywhere, in the posting's
 * own currency). From job_data_facts(), about 5 ms.
 *
 * This replaces the "What the data says about this job" section for readers.
 * In its first ten days that section rendered 1,702 times, was scrolled into
 * view 316 times and clicked 4 times; none of the clickers applied. The pay
 * comparison was the one fact that bears on the decision in front of the
 * reader, so it moved next to the number it qualifies. Crawlers still get
 * every fact: seo_for_job() appends job_data_facts_html() to the page HTML.
 *
 * Renders nothing unless the posting states pay and a median exists.
 */

interface Market {
  scope: 'metro' | 'all'; role_key: string; metro_key: string | null;
  role: string | null; metro: string | null; median: number; n: number; currency: string;
  posting_lo: number | null; posting_hi: number | null;
}

const kMoney = (v: number, ccy: string) => `${ccy === 'CAD' ? 'CA$' : '$'}${Math.round(v / 1000)}k`;

export function JobPayLine({ jobId }: { jobId: string }) {
  const { data: m } = useQuery({
    queryKey: ['job_data_facts', jobId],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as unknown as (fn: string, a: object) => Promise<{ data: unknown; error: unknown }>)(
        'job_data_facts', { p_job_id: jobId });
      if (error) throw error;
      return ((data as { market?: Market | null } | null)?.market ?? null);
    },
    enabled: !!jobId,
    staleTime: 1000 * 60 * 30,
  });

  if (!m || m.posting_lo == null || m.posting_hi == null || !m.median) return null;

  const mid = (m.posting_lo + m.posting_hi) / 2;
  const pct = Math.round(((mid - m.median) / m.median) * 100);
  const median = kMoney(m.median, m.currency);
  const role = m.role ?? 'this role';
  const where = m.scope === 'metro' && m.metro ? ` in ${m.metro}` : '';
  const lead = Math.abs(pct) <= 3
    ? `In line with the ${median} median`
    : `${Math.abs(pct)}% ${pct > 0 ? 'above' : 'below'} the ${median} median`;
  const href = `/data/explorer?view=detail&role=${encodeURIComponent(m.role_key)}${m.scope === 'metro' && m.metro_key ? `&metro=${encodeURIComponent(m.metro_key)}` : ''}`;

  return (
    <p className="salary-card__market">
      {lead} for {role} jobs{where}, across {m.n.toLocaleString('en-US')} postings.{' '}
      <Link to={href} onClick={() => siteEvents.act('pay_line_click')}>See the market</Link>
    </p>
  );
}
